import PropTypes from "prop-types";
import { useMemo } from "react";

import { Box, Paper, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { EntityTypeEnum } from "@/features/outlet-chart-of-account-transactions";
import { OutletRoleEnum } from "@/features/outlets";
import { formatNumber } from "@/features/shared";

const getEntityLabel = (transaction) => {
  if (!transaction.entityType) return null;

  let entity;
  let typeLabel;

  switch (transaction.entityType) {
    case EntityTypeEnum.SUPPLIER:
      entity = transaction.supplier;
      typeLabel = "Pemasok";
      break;
    case EntityTypeEnum.CUSTOMER:
      entity = transaction.customer;
      typeLabel = "Pelanggan";
      break;
    case EntityTypeEnum.OUTLET:
      entity = transaction.outlet;
      typeLabel =
        entity?.role === OutletRoleEnum.HUB ? "Outlet Hub" : "Outlet Retail";
      break;
    default:
      return null;
  }

  if (!entity) return null;
  return `${typeLabel}: ${entity.name}`;
};

// Custom Footer Component
const CustomFooter = ({ totalDebit, totalCredit }) => {
  return (
    <Box
      sx={{
        borderTop: "1px solid",
        borderColor: "divider",
        p: 1,
        display: "flex",
        alignItems: "center",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ flex: 2 }}>
        <Typography variant="subtitle2" sx={{
          fontWeight: "bold"
        }}>
          Total
        </Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: "right" }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: "bold",
            pr: 1
          }}>
          {formatNumber(totalDebit)}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: "right" }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: "bold",
            pr: 1
          }}>
          {formatNumber(totalCredit)}
        </Typography>
      </Box>
      <Box sx={{ flex: 2 }} />
    </Box>
  );
};

CustomFooter.propTypes = {
  totalDebit: PropTypes.number.isRequired,
  totalCredit: PropTypes.number.isRequired,
};

export const AccountTransactionsSection = ({
  outletChartOfAccountTransactions,
}) => {
  const columns = [
    {
      field: "outletChartOfAccount",
      headerName: "Akun",
      flex: 2,
      valueGetter: (value) => value.code,
      renderCell: ({ row }) => {
        const { outletChartOfAccount } = row;
        const entityLabel = getEntityLabel(row);
        return (
          <Stack
            sx={{
              justifyContent: "center",
              py: 1
            }}>
            <Typography variant="body2">
              {outletChartOfAccount.code} - {outletChartOfAccount.name}
            </Typography>
            {entityLabel && (
              <Typography variant="caption" sx={{
                color: "text.secondary"
              }}>
                {entityLabel}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: "debit",
      headerName: "Debit",
      flex: 1,
      type: "number",
      valueGetter: (value, row) => {
        return row.amount > 0 ? row.amount : 0;
      },
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "credit",
      headerName: "Kredit",
      flex: 1,
      type: "number",
      valueGetter: (value, row) => {
        return row.amount < 0 ? Math.abs(row.amount) : 0;
      },
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 2,
      valueGetter: (value) => value || "-",
    },
  ];

  // Calculate totals
  const totalDebit = useMemo(
    () =>
      outletChartOfAccountTransactions.reduce(
        (sum, transaction) =>
          transaction.amount > 0 ? sum + transaction.amount : sum,
        0,
      ),
    [outletChartOfAccountTransactions],
  );

  const totalCredit = useMemo(
    () =>
      outletChartOfAccountTransactions.reduce(
        (sum, transaction) =>
          transaction.amount < 0 ? sum + Math.abs(transaction.amount) : sum,
        0,
      ),
    [outletChartOfAccountTransactions],
  );

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="subtitle1" sx={{
        mb: 2
      }}>
        Transaksi Akun
      </Typography>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <DataGrid
          rows={outletChartOfAccountTransactions}
          columns={columns}
          getRowHeight={() => "auto"}
          hideFooter={false}
          disableRowSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [{ field: "outletChartOfAccount", sort: "asc" }],
            },
          }}
          slots={{
            footer: CustomFooter,
          }}
          slotProps={{
            footer: {
              totalDebit,
              totalCredit,
            },
          }}
        />
      </div>
    </Paper>
  );
};

AccountTransactionsSection.propTypes = {
  outletChartOfAccountTransactions: PropTypes.array.isRequired,
};
